import { useState, useEffect, useCallback, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Sidebar from "@/components/layout/Sidebar";
import Header from "@/components/layout/Header";
import AllisonPanel from "@/components/AllisonPanel";
import { LayoutProvider, useLayout } from "@/context/LayoutContext";
import { ContextPanel } from "@/components/shared";
import { api } from "@/lib/api";

function Shell() {
  const { collapsed, toggleSidebar, panel, closeContextPanel } = useLayout();
  const { pathname } = useLocation();
  const fullBleed = pathname === "/communications/center" || pathname === "/communications/inbox" || pathname === "/communications/leads" || pathname === "/communications/clients" || pathname === "/service-library";
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [allisonOpen, setAllisonOpen] = useState(false);
  const [badges, setBadges] = useState({});
  const mainRef = useRef(null);

  // Always land at the top of the page when navigating to a new route.
  // Non-fullBleed pages scroll the window; fullBleed pages scroll <main>. Reset both.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
    mainRef.current?.scrollTo({ top: 0, left: 0 });
  }, [pathname]);

  const loadBadges = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/stats");
      const byKey = Object.fromEntries((data?.cards || []).map((c) => [c.key, c.value]));
      let leadWaiting = 0, clientWaiting = 0;
      try {
        const { data: cc } = await api.get("/communications/counts");
        leadWaiting = cc?.staff_action_buckets?.lead_waiting || 0;
        clientWaiting = cc?.staff_action_buckets?.client_waiting || 0;
      } catch (e) { /* ignore */ }
      // Revision Requests pending count — included in Comms parent badge now that it's off the sidebar
      let revisionPending = 0;
      try {
        const { data: rv } = await api.get("/communications/revision-requests?status=pending");
        revisionPending = (rv?.items || []).length;
      } catch (e) { /* ignore */ }
      // Live Ops badge — unanswered inbound items (messages + webchat + appointments)
      let liveOpsBadge = 0;
      try {
        const { data: ob } = await api.get("/notifications/ops-badge");
        liveOpsBadge = ob?.count || 0;
      } catch (e) { /* ignore */ }
      setBadges({
        documents: byKey.outstanding_documents || 0,
        communications: (leadWaiting + clientWaiting + revisionPending) || byKey.unread_messages || 0,
        comm_leads: leadWaiting,
        comm_clients: clientWaiting,
        comm_revision: revisionPending,
        live_ops: liveOpsBadge,
        ops_center: liveOpsBadge,
      });
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { loadBadges(); const id = setInterval(loadBadges, 20000); return () => clearInterval(id); }, [loadBadges]);

  return (
    <div className="h-screen overflow-hidden flex bg-slate-50">
      <Sidebar
        collapsed={collapsed}
        onToggleCollapse={toggleSidebar}
        badges={badges}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <Header onOpenMobileNav={() => setMobileNavOpen(true)} onOpenAllison={() => setAllisonOpen(true)} />
        <main ref={mainRef} className={`flex-1 min-h-0 ${fullBleed ? "overflow-hidden" : "overflow-y-auto"} tf-scroll`} data-testid="app-main">
          {fullBleed ? (
            <div className="h-full"><Outlet /></div>
          ) : (
            <div className="p-5 md:p-8 max-w-[1600px] mx-auto w-full animate-in fade-in slide-in-from-bottom-2 duration-300">
              <Outlet />
            </div>
          )}
        </main>
      </div>

      <AllisonPanel open={allisonOpen} onOpenChange={setAllisonOpen} />
      <ContextPanel open={panel.open} onOpenChange={(o) => !o && closeContextPanel()} title={panel.title}>
        {panel.content}
      </ContextPanel>
    </div>
  );
}

export default function AppLayout() {
  return (
    <LayoutProvider>
      <Shell />
    </LayoutProvider>
  );
}
